import React, { useEffect } from 'react';
import NumberFormat from 'react-number-format';
// external imports
import {
  AppBar,
  Toolbar,
  FormControl,
  InputLabel,
  Input,
  Divider,
  Button,
  NativeSelect,
  FormHelperText,
  Grid,
} from '@material-ui/core';
// internal imports
import { useSetState } from '~/app/Utility/customHooks';
// redux imports
import { useSelector, useDispatch } from 'react-redux';
import { actions as setupPageActions } from '~/app/redux/setupPages/duck';
import { formHasErrors } from '../databaseSetupHelper';
// styling imports
import { withStyles } from '@material-ui/core/styles';
import { style } from './css/upsertCareProviderStyle';
import './css/upsertCareProvider.css';

const UpsertCareProvider = props => {
  const { classes } = props;
  const dispatch = useDispatch();

  const [state, setState] = useSetState({
    inputErrors: {},
    isSubmitDisabled: false,
  });

  const { isEditingRecord, currentEditRecord } = useSelector(state => ({
    isEditingRecord: state.setupPageState.isEditingRecord,
    currentEditRecord: state.setupPageState.currentEditRecord,
  }));

  useEffect(() => {
    setState({
      isSubmitDisabled: formHasErrors(currentEditRecord, handleSetErrors),
    });
  }, [currentEditRecord]);

  const handleSetErrors = errors => {
    setState({ inputErrors: errors }); // set any errors to the local state
  };

  const handleInputChange = e => {
    const name = e.target.name;
    e.persist();
    dispatch(setupPageActions.updateCurrentEditRecord(e));
    // Clear out inputErrors if user is modifying field value
    const tempErrors = state.inputErrors;
    if (tempErrors[name]) {
      delete tempErrors[name];
      setState({
        inputErrors: tempErrors,
      });
    }
  };

  const handleUpsert = () => {
    currentEditRecord.provider_phone = currentEditRecord.provider_phone.replace(/\D/g, '');
    props.handleUserDBUpsert(currentEditRecord);
  };

  const validateFields = () => {
    if (
      currentEditRecord.address1 == null ||
      currentEditRecord.address1 === '' ||
      currentEditRecord.postal_code == null ||
      currentEditRecord.postal_code === '' ||
      currentEditRecord.provider_code == null ||
      currentEditRecord.provider_code === '' ||
      currentEditRecord.provider_name == null ||
      currentEditRecord.provider_name === '' ||
      currentEditRecord.city == null ||
      currentEditRecord.city === '' ||
      currentEditRecord.state_code == null ||
      currentEditRecord.state_code === ''
    ) {
      return false;
    }
    return true;
  };

  return (
    <div>
      <AppBar position="static">
        <Toolbar classes={{ root: classes.toolbar }}>
          {isEditingRecord ? 'Edit ' : 'Add New '}
          Care Provider
        </Toolbar>
      </AppBar>
      <form className="form-margin">
        <Grid container spacing={8}>
          <Grid item xs={8} className="section-heading">
            Care Provider Information
          </Grid>
          <Grid item xs={6}>
            <FormControl>
              <InputLabel classes={{ root: classes.labelStyles }} required={false} shrink>
                Care Provider ID
              </InputLabel>
              <Input
                id="txtEINIDUpsertCareProviderProvCode"
                name="provider_code"
                value={currentEditRecord.provider_code}
                onChange={handleInputChange}
                classes={{ root: classes.inputStyle }}
                error={
                  !currentEditRecord.provider_code ||
                  currentEditRecord.provider_code === '' ||
                  state.inputErrors?.provider_code !== undefined
                }
                inputProps={{
                  maxLength: '9',
                }}
                disableUnderline
              />
              {state.inputErrors?.provider_code && (
                <FormHelperText>{state.inputErrors.provider_code}</FormHelperText>
              )}
            </FormControl>
          </Grid>
          <Grid item xs={6}>
            <FormControl>
              <InputLabel classes={{ root: classes.labelStyles }} required={false} shrink>
                Provider ID Type
              </InputLabel>
              <NativeSelect
                id="txtNameControlUpsertCareProvider"
                name="provider_type"
                value={
                  !currentEditRecord.provider_type ? 'S' : currentEditRecord.provider_type.trim()
                }
                classes={{ root: classes.inputStyleNativeSelectProvider }}
                onChange={handleInputChange}
                disableUnderline
              >
                <option value={'S'}>SSN</option>
                <option value={'E'}>EIN</option>
              </NativeSelect>
            </FormControl>
          </Grid>
          <Grid item xs={6}>
            <FormControl>
              <InputLabel classes={{ root: classes.labelStyles }} required={false} shrink>
                Name Control
              </InputLabel>
              <Input
                id="txtNameControlUpsertCareProviderNamecrtl"
                name="provider_name_ctrl"
                value={currentEditRecord.provider_name_ctrl}
                onChange={handleInputChange}
                classes={{ root: classes.inputStyle }}
                error={state.inputErrors?.provider_name_ctrl !== undefined}
                inputProps={{
                  maxLength: '4',
                }}
                disableUnderline
              />
              {state.inputErrors?.provider_name_ctrl && (
                <FormHelperText>{state.inputErrors.provider_name_ctrl}</FormHelperText>
              )}
            </FormControl>
          </Grid>
          <Grid item xs={6}>
            <FormControl>
              <InputLabel classes={{ root: classes.labelStyles }} required={false} shrink>
                Care Provider Name
              </InputLabel>
              <Input
                id="txtNameControlUpsertCareProviderName"
                name="provider_name"
                value={currentEditRecord.provider_name}
                onChange={handleInputChange}
                classes={{ root: classes.inputStyle }}
                error={
                  !currentEditRecord.provider_name ||
                  currentEditRecord.provider_name === '' ||
                  state.inputErrors?.provider_name !== undefined
                }
                inputProps={{
                  maxLength: '75',
                }}
                disableUnderline
              />
              {state.inputErrors?.provider_name && (
                <FormHelperText>{state.inputErrors.provider_name}</FormHelperText>
              )}
            </FormControl>
          </Grid>
          <Grid item xs={6}>
            <FormControl>
              <InputLabel classes={{ root: classes.labelStyles }} required={false} shrink>
                Care Provider Phone Number
              </InputLabel>
              <NumberFormat
                id="txtNameControlUpsertCareProviderPhone"
                name="provider_phone"
                value={currentEditRecord.provider_phone}
                onChange={handleInputChange}
                classes={{ root: classes.inputStyle }}
                error={!currentEditRecord.provider_phone || currentEditRecord.provider_phone === ''}
                format="(###) ###-####"
                customInput={Input}
                disableUnderline
              />
            </FormControl>
          </Grid>
        </Grid>
        <br />
        <Divider className="divider-style-margin-top-1em" />
        <br />
        <Grid container spacing={8}>
          <Grid item xs={8} className="section-heading">
            Address
          </Grid>
          <Grid item xs={6}>
            <FormControl>
              <InputLabel classes={{ root: classes.labelStyles }} required={false} shrink>
                Address
              </InputLabel>
              <Input
                id="txtEINIDUpsertCareProviderAddress"
                name="address1"
                value={currentEditRecord.address1}
                onChange={handleInputChange}
                classes={{ root: classes.inputStyle }}
                error={
                  !currentEditRecord.address1 ||
                  currentEditRecord.address1 === '' ||
                  state.inputErrors?.address1 !== undefined
                }
                inputProps={{
                  maxLength: '75',
                }}
                disableUnderline
              />
              {state.inputErrors?.address1 && (
                <FormHelperText>{state.inputErrors.address1}</FormHelperText>
              )}
            </FormControl>
          </Grid>
          <Grid item xs={6}>
            <FormControl>
              <InputLabel classes={{ root: classes.labelStyles }} required={false} shrink>
                City
              </InputLabel>
              <Input
                id="txtNameControlUpsertCareProviderCity"
                name="city"
                value={currentEditRecord.city}
                onChange={handleInputChange}
                classes={{ root: classes.inputStyle }}
                error={
                  !currentEditRecord.city ||
                  currentEditRecord.city === '' ||
                  state.inputErrors?.city !== undefined
                }
                inputProps={{
                  maxLength: '75',
                }}
                disableUnderline
              />
              {state.inputErrors?.city && <FormHelperText>{state.inputErrors.city}</FormHelperText>}
            </FormControl>
          </Grid>

          <Grid item xs={6}>
            <FormControl>
              <InputLabel classes={{ root: classes.labelStyles }} shrink>
                State
              </InputLabel>
              <Input
                id="txtNameControlUpsertCareProviderState"
                name="state_code"
                value={currentEditRecord.state_code || ''}
                onChange={handleInputChange}
                classes={{ root: classes.inputStyle }}
                error={!currentEditRecord.state_code || state.inputErrors?.state_code !== undefined}
                inputProps={{
                  maxLength: '2',
                }}
                disableUnderline
              />
              <FormHelperText>{state.inputErrors?.state_code}</FormHelperText>
            </FormControl>
          </Grid>
          <Grid item xs={6}>
            <FormControl>
              <InputLabel classes={{ root: classes.labelStyles }} required={false} shrink>
                Zip Code
              </InputLabel>
              <Input
                id="txtNameControlUpsertCareProviderZipCode"
                name="postal_code"
                value={currentEditRecord.postal_code}
                onChange={handleInputChange}
                classes={{ root: classes.inputStyle }}
                error={
                  !currentEditRecord.postal_code ||
                  currentEditRecord.postal_code === '' ||
                  state.inputErrors?.postal_code !== undefined
                }
                inputProps={{
                  maxLength: '12',
                }}
                disableUnderline
              />
              {state.inputErrors?.postal_code && (
                <FormHelperText>{state.inputErrors.postal_code}</FormHelperText>
              )}
            </FormControl>
          </Grid>
        </Grid>
        <Grid
          container
          spacing={8}
          className="care-provider-button-wrapper"
          style={{
            flexWrap: 'nowrap',
          }}
        >
          <Grid item xs={9}>
            <Button
              id="btnCancelAddUUpsertCareProvider"
              color="primary"
              onClick={props.handleCloseUpsertModal}
              classes={{ root: classes.button }}
            >
              Cancel
            </Button>
          </Grid>
          <Grid item>
            <Button
              id="btnUpsertCareProvider"
              color="primary"
              classes={{ root: classes.button }}
              onClick={handleUpsert}
              disabled={!validateFields() || state.isSubmitDisabled}
            >
              {isEditingRecord ? 'Save' : 'Add'}
            </Button>
          </Grid>
        </Grid>
        <br />
      </form>
    </div>
  );
};

export default withStyles(style)(UpsertCareProvider);
